import { useEffect, useRef, useState } from "react";
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X, ImagePlus, RefreshCw, GripVertical,
} from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { compressAndUpload } from "../../lib/uploadImage";

const TYPES = ["Re-roof", "New roof", "Repairs", "Gutters & downpipes", "Commercial remedial", "Other"];
const CLIENT_TYPES = ["", "Residential", "Commercial", "Strata"];

const EMPTY = {
  title: "", suburb: "", project_type: "Re-roof", status: "completed",
  summary: "", description: "", photos: [], before_url: "", after_url: "",
  client_type: "", date: "", visibility: "hidden",
};

const cls = "w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm text-[#1A2421] focus:outline-none focus:ring-2 focus:ring-[#62B848]";
const lbl = "text-xs font-bold uppercase tracking-wide text-[#4A5550] mb-1.5 block";

function StatusBadge({ status }) {
  return status === "in_progress" ? (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#62B848] text-white">In Progress</span>
  ) : (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#224733] text-white">Completed</span>
  );
}

function ProjectForm({ initial, onDone, onCancel }) {
  const [p, setP] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef(null);
  const set = (k) => (e) => setP((d) => ({ ...d, [k]: e.target.value }));

  const addPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const url = await compressAndUpload(f);
        setP((d) => ({ ...d, photos: [...d.photos, url] }));
      }
    } catch (e) { console.error(e); alert("One or more photos failed to upload. Try again."); }
    setUploading(false);
  };

  const uploadSingle = (key) => async (files) => {
    if (!files?.[0]) return;
    setUploading(true);
    try { const url = await compressAndUpload(files[0]); setP((d) => ({ ...d, [key]: url })); }
    catch (e) { console.error(e); alert("Upload failed. Try again."); }
    setUploading(false);
  };

  const removePhoto = (i) => setP((d) => ({ ...d, photos: d.photos.filter((_, idx) => idx !== i) }));
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setP((d) => {
      const next = [...d.photos];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return { ...d, photos: next };
    });
    dragIdx.current = null;
  };

  const save = async (publish) => {
    if (!p.title.trim()) { alert("Give the project a title."); return; }
    setSaving(true);
    const body = { ...p, visibility: publish ? "published" : (initial?.id ? p.visibility : "hidden") };
    if (publish) body.visibility = "published";
    try {
      if (initial?.id) await api.put(`/admin/projects/${initial.id}`, body);
      else await api.post("/admin/projects", body);
      onDone();
    } catch (e) { console.error(e); alert("Save failed."); }
    setSaving(false);
  };

  return (
    <div data-testid="project-form" className="bg-white rounded-3xl border border-black/5 p-6 md:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-extrabold text-2xl text-[#1A2421]">{initial?.id ? "Edit project" : "Add project"}</h3>
        <button data-testid="project-cancel" onClick={onCancel} className="grid place-items-center w-10 h-10 rounded-full bg-[#F1F5F0] hover:bg-black/5"><X size={18} /></button>
      </div>

      <div><label className={lbl}>Project title *</label><input data-testid="pf-title" className={cls} value={p.title} onChange={set("title")} placeholder="e.g. Heritage re-roof — South Perth" /></div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={lbl}>Suburb</label><input data-testid="pf-suburb" className={cls} value={p.suburb} onChange={set("suburb")} placeholder="South Perth" /></div>
        <div><label className={lbl}>Project type</label><select data-testid="pf-type" className={cls} value={p.project_type} onChange={set("project_type")}>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><label className={lbl}>Status</label><select data-testid="pf-status" className={cls} value={p.status} onChange={set("status")}><option value="in_progress">In Progress</option><option value="completed">Completed</option></select></div>
        <div><label className={lbl}>Client type</label><select data-testid="pf-clienttype" className={cls} value={p.client_type} onChange={set("client_type")}>{CLIENT_TYPES.map((t) => <option key={t} value={t}>{t || "— none —"}</option>)}</select></div>
        <div><label className={lbl}>{p.status === "completed" ? "Date completed" : "Date started"}</label><input type="date" data-testid="pf-date" className={cls} value={p.date} onChange={set("date")} /></div>
        <div><label className={lbl}>Visibility</label><select data-testid="pf-visibility" className={cls} value={p.visibility} onChange={set("visibility")}><option value="hidden">Hidden (draft)</option><option value="published">Published</option></select></div>
      </div>

      <div><label className={lbl}>Short summary (shown on card)</label><input data-testid="pf-summary" className={cls} value={p.summary} onChange={set("summary")} placeholder="1–2 lines about the job" /></div>
      <div><label className={lbl}>Full description (project page)</label><textarea data-testid="pf-description" rows={4} className={`${cls} resize-none`} value={p.description} onChange={set("description")} placeholder="What the job involved, materials, challenges, results…" /></div>

      {/* Photos */}
      <div>
        <label className={lbl}>Photos — first is the cover · drag to reorder</label>
        <div className="flex flex-wrap gap-3">
          {p.photos.map((url, i) => (
            <div key={url + i} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              className="relative group w-28 h-28 rounded-2xl overflow-hidden border border-black/10 cursor-move">
              <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#62B848] text-white">COVER</span>}
              <span className="absolute bottom-1 left-1 text-white/90"><GripVertical size={14} /></span>
              <button data-testid={`pf-remove-photo-${i}`} onClick={() => removePhoto(i)} className="absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100"><X size={13} /></button>
            </div>
          ))}
          <label className="w-28 h-28 rounded-2xl border-2 border-dashed border-black/15 grid place-items-center cursor-pointer hover:border-[#62B848] text-[#4A5550]">
            {uploading ? <Loader2 size={22} className="animate-spin text-[#62B848]" /> : <div className="flex flex-col items-center gap-1"><ImagePlus size={22} /><span className="text-[10px] font-semibold">Add photos</span></div>}
            <input data-testid="pf-photos-input" type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} />
          </label>
        </div>
      </div>

      {/* Before / After */}
      <div className="grid sm:grid-cols-2 gap-4">
        {[["before_url", "Before photo (optional)"], ["after_url", "After photo (optional)"]].map(([key, label]) => (
          <div key={key}>
            <label className={lbl}>{label}</label>
            {p[key] ? (
              <div className="relative w-full h-40 rounded-2xl overflow-hidden border border-black/10">
                <img src={mediaUrl(p[key])} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setP((d) => ({ ...d, [key]: "" }))} className="absolute top-2 right-2 grid place-items-center w-7 h-7 rounded-full bg-black/60 text-white"><X size={14} /></button>
              </div>
            ) : (
              <label className="w-full h-40 rounded-2xl border-2 border-dashed border-black/15 grid place-items-center cursor-pointer hover:border-[#62B848] text-[#4A5550]">
                <div className="flex flex-col items-center gap-1"><ImagePlus size={22} /><span className="text-xs font-semibold">Upload</span></div>
                <input data-testid={`pf-${key === "before_url" ? "before" : "after"}-input`} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => uploadSingle(key)(e.target.files)} />
              </label>
            )}
          </div>
        ))}
      </div>
      {p.before_url && p.after_url && <p className="text-xs text-[#62B848] font-semibold">Before/after pair set — renders as the draggable comparison slider on the project page.</p>}

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-black/5">
        <button data-testid="project-save-draft" onClick={() => save(false)} disabled={saving || uploading} className="px-5 py-2.5 rounded-full bg-[#F1F5F0] text-[#224733] font-bold text-sm hover:bg-black/5 disabled:opacity-50">Save {initial?.id ? "" : "as draft"}</button>
        <button data-testid="project-save-publish" onClick={() => save(true)} disabled={saving || uploading} className="px-6 py-2.5 rounded-full bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2">{saving && <Loader2 size={15} className="animate-spin" />}Publish to website</button>
        {uploading && <span className="text-sm text-[#4A5550]">Uploading photos…</span>}
      </div>
    </div>
  );
}

export function ProjectsManager() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | project (edit)

  const load = () => api.get("/admin/projects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggleVisibility = async (p) => {
    await api.put(`/admin/projects/${p.id}`, { visibility: p.visibility === "published" ? "hidden" : "published" });
    load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    load();
  };

  if (editing !== null) {
    return <ProjectForm initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-extrabold text-2xl text-[#1A2421]">Projects</h2>
          <p className="text-sm text-[#4A5550]">Your work — published projects appear on the Our Work page &amp; homepage. Nothing goes live until you Publish.</p>
        </div>
        <button data-testid="projects-add-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold text-sm px-5 py-2.5 rounded-full"><Plus size={17} /> Add project</button>
      </div>

      {items === null ? (
        <div className="p-12 text-center text-[#4A5550]"><RefreshCw size={22} className="animate-spin mx-auto mb-3" /> Loading projects…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl border border-black/5 p-12 text-center">
          <div className="grid place-items-center w-16 h-16 rounded-2xl bg-[#62B848]/15 text-[#62B848] mx-auto mb-5"><ImagePlus size={30} /></div>
          <h3 className="font-display font-extrabold text-xl text-[#1A2421] mb-2">No projects yet</h3>
          <p className="text-[#4A5550] max-w-sm mx-auto">Add your first job — photos, before/after, a short summary — and publish it to show it off on the website.</p>
          <button onClick={() => setEditing({})} className="mt-6 inline-flex items-center gap-2 bg-[#224733] text-white font-bold px-6 py-3 rounded-full"><Plus size={17} /> Add project</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p) => {
            const cover = p.photos?.[0] || p.after_url || p.before_url;
            return (
              <div key={p.id} data-testid={`project-card-${p.id}`} className="bg-white rounded-3xl border border-black/5 overflow-hidden flex flex-col">
                <div className="relative h-44 bg-[#1A2421]">
                  {cover ? <img src={mediaUrl(cover)} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-white/40"><ImagePlus size={30} /></div>}
                  <div className="absolute top-3 left-3 flex gap-2"><StatusBadge status={p.status} /></div>
                  <span className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${p.visibility === "published" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>{p.visibility === "published" ? "Published" : "Hidden"}</span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="font-display font-bold text-[#1A2421] leading-tight">{p.title}</div>
                  <div className="text-xs text-[#4A5550] mt-1">{[p.suburb, p.project_type].filter(Boolean).join(" · ")}</div>
                  {p.summary && <p className="text-sm text-[#4A5550] mt-2 line-clamp-2">{p.summary}</p>}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-black/5">
                    <button data-testid={`project-edit-${p.id}`} onClick={() => setEditing(p)} className="inline-flex items-center gap-1.5 bg-[#F1F5F0] text-[#224733] font-bold text-xs px-3 py-2 rounded-full hover:bg-black/5"><Pencil size={13} /> Edit</button>
                    <button data-testid={`project-hide-${p.id}`} onClick={() => toggleVisibility(p)} className="inline-flex items-center gap-1.5 bg-[#F1F5F0] text-[#224733] font-bold text-xs px-3 py-2 rounded-full hover:bg-black/5">{p.visibility === "published" ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Publish</>}</button>
                    <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p)} className="ml-auto inline-flex items-center gap-1.5 bg-[#F1F5F0] text-red-600 font-bold text-xs px-3 py-2 rounded-full hover:bg-red-50"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
